import { useEffect, useRef, useState } from 'react';
import { getNextVersionCheckDelayMs } from '@client/helpers/app-version-reload.helpers';

type ScheduledAppVersionCheckOptions = {
  isVersionMismatchPage: boolean;
};

/**
 * Asks `checkVersion` again when the user returns to this document, and once
 * an hour while the document stays visible.
 * Focus and visibility in the same turn share one check.
 * `checkVersion` identity changes do not reset that timer.
 * Calling `checkVersion` does not reload; the caller reloads only on a new server version.
 */
export const useScheduledAppVersionCheck = (
  checkVersion: () => void,
  { isVersionMismatchPage }: ScheduledAppVersionCheckOptions,
): void => {
  const checkRef = useRef(checkVersion);
  checkRef.current = checkVersion;

  const [documentVisible, setDocumentVisible] = useState(
    () => document.visibilityState === 'visible',
  );
  const [pollGeneration, setPollGeneration] = useState(0);

  useEffect(() => {
    let returnCheckQueued = false;

    // Browsers often emit focus and visibilitychange in the same turn when a tab returns.
    const requestReturnCheck = () => {
      if (returnCheckQueued) return;
      returnCheckQueued = true;
      queueMicrotask(() => {
        returnCheckQueued = false;
      });
      checkRef.current();
    };

    const onFocus = () => {
      requestReturnCheck();
    };
    const onVisibility = () => {
      const visible = document.visibilityState === 'visible';
      setDocumentVisible(visible);
      if (!visible) return;
      requestReturnCheck();
    };
    // Back-forward cache restores the old bundle without a fresh load.
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      requestReturnCheck();
    };

    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Reschedule from the current delay. checkVersion is read from a ref so a new
  // callback does not restart the hour wait.
  useEffect(() => {
    const delay = getNextVersionCheckDelayMs({
      isVersionMismatchPage,
      documentVisible,
    });
    if (delay === null) return;

    const id = window.setTimeout(() => {
      checkRef.current();
      setPollGeneration((generation) => generation + 1);
    }, delay);
    return () => window.clearTimeout(id);
  }, [documentVisible, isVersionMismatchPage, pollGeneration]);
};
